import classNames from "classnames/bind";
import useModalContext from "common/providers/modal/useModalContext";
import { Sidebar } from "common/providers/modal/useSidebar";
import dayjs from "dayjs";
import { ResolvedPDPPage } from "resolvers/pages/pdpPageResolver";

import styles from "./WidgetsRow.module.scss";
import CollectionTransferDeliveryWidget, {
    CollectionTransferDeliveryWidgetProps,
} from "./components/CollectionTransferDeliveryWidget";
import EstimateAvailableWidget from "./components/EstimateAvailableWidget";
import FinanceDealWidget, { FinanceDealWidgetProps } from "./components/FinanceDealWidget";
import PartExchangeWidget, { PartExchangeWidgetProps } from "./components/PartExchangeWidget";

const cx = classNames.bind(styles);

export type WidgetsRowProps = {
    components: ({ partExchangeWidget: PartExchangeWidgetProps } & {
        collectionTransferDeliveryWidget: CollectionTransferDeliveryWidgetProps;
    } & { financeDealWidget: FinanceDealWidgetProps })[];
    comingSoon?: {
        dateBecameComingSoon: Date;
        averageDaysToPrep: number;
        openSidebarCallback: () => Sidebar;
    };
    content: ResolvedPDPPage["widgetRowContent"];
};

const WidgetsRow = ({ components, comingSoon, content }: WidgetsRowProps) => {
    const { useSidebar } = useModalContext();
    const { openSidebar } = useSidebar();

    const estimateAvailable =
        comingSoon &&
        ((): {
            title: string;
            message: string;
        } => {
            const expectedDate = dayjs(comingSoon.dateBecameComingSoon)
                .add(comingSoon.averageDaysToPrep, "days")
                .toDate();
            const diffDays = dayjs(expectedDate).diff(dayjs(), "days");
            const day = dayjs(expectedDate).format("Do");
            const month = dayjs(expectedDate).format("MMMM");

            const {
                lowBracketTitle,
                lowBracketDescription,
                midBracketTitle,
                midBracketDescription,
                highBracketTitle,
                highBracketDescription,
            } = content;

            switch (true) {
                case diffDays >= 1 && diffDays <= 3:
                    return {
                        title: lowBracketTitle,
                        message: lowBracketDescription.replace(
                            "{EstimatedDate}",
                            `${day} of ${month}`
                        ),
                    };
                case diffDays >= 4 && diffDays <= 7:
                    return {
                        title: midBracketTitle,
                        message: midBracketDescription.replace(
                            "{EstimatedDate}",
                            `${day} of ${month}`
                        ),
                    };
                default:
                    return {
                        title: highBracketTitle,
                        message: highBracketDescription.replace(
                            "{EstimatedDate}",
                            `${day} of ${month}`
                        ),
                    };
            }
        })();

    const orderedLayout =
        components?.map((props) => {
            switch (true) {
                case !!props?.partExchangeWidget:
                    return (
                        <div key="partExchangeWidget" className={cx("widgetCol", "widgetColFirst")}>
                            <PartExchangeWidget {...props.partExchangeWidget} />
                        </div>
                    );
                case !!props?.collectionTransferDeliveryWidget:
                    return (
                        <div
                            key="collectionTransferDeliveryWidget"
                            className={cx(
                                "widgetCol",
                                "widgetColMiddle",
                                estimateAvailable && "widgetComingSoonOrder"
                            )}
                        >
                            {estimateAvailable ? (
                                <EstimateAvailableWidget
                                    heading={estimateAvailable.title}
                                    body={`${estimateAvailable.message}`}
                                    cta={{
                                        text: content.alertSidePanel.alertCtaText,
                                        onClick: () => {
                                            openSidebar(comingSoon.openSidebarCallback());
                                        },
                                    }}
                                />
                            ) : (
                                <CollectionTransferDeliveryWidget
                                    {...props.collectionTransferDeliveryWidget}
                                />
                            )}
                        </div>
                    );
                case !!props?.financeDealWidget:
                    return (
                        <div key="financeDealWidget" className={cx("widgetCol", "widgetColLast")}>
                            <FinanceDealWidget {...props.financeDealWidget} />
                        </div>
                    );
                default:
                    return null;
            }
        }) ?? null;

    return <div className={cx("widgets")}>{orderedLayout}</div>;
};

export default WidgetsRow;
